import intl from 'react-intl-universal';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { useEffect, useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import { Table, Typography } from '@getsynapse/design-system';
import { DATE, PATHS } from 'utils/constants';
import { formatProjectNumber } from 'utils/formatters';
import {
  PilotPagesToTrack,
  Project,
  SortingType,
  StatusInlineEditingEntityType,
} from 'utils/customTypes';
import {
  getOwnerColumn,
  getStatusColumn,
} from 'Pages/ProjectsListPage/helpers/tableColumnsValues';
import { getProgress } from 'Pages/GoalDetailsPage/helpers/helper';
import { GOAL_PROJECTS_TABLE_COLUMNS } from 'utils/constants/strategyGoals';
import {
  fetchGoalProjects,
  selectFormattedGoalProjects,
} from 'state/GoalProjects/GoalProjectsSlice';
import {
  getProjectPermissions,
  projectPermissionsSelectors,
} from 'state/normalized/projectPermissions/projectPermissionsSlice';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LinkTableRowCell } from 'Atoms/LinkTableRowCell';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell/StatusTableRowCell';
import { useUpdateProjectStatus } from 'Pages/ProjectsListPage/hooks/useUpdateProjectStatus/useUpdateProjectStatus';
import CloseProject from 'Pages/ProjectPage/components/CloseProject/CloseProject';
import CancelProject from 'Pages/ProjectPage/components/CancelProject/CancelProject';
import PutProjectOnHoldModal from 'Pages/ProjectPage/components/PutProjectOnHoldModal/PutProjectOnHoldModal';
import { bulkUpdateProjects } from 'api/projects';
import {
  fetchProject,
  getOriginalProjectData,
  resetProject,
} from 'state/Project/projectSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { RootState, useAppThunkDispatch } from 'state/store';

const GoalProjectsTable = ({ goalId }: { goalId: string }) => {
  const dispatch = useAppThunkDispatch();
  const history = useHistory();

  const { inlineEditingProjectStatusGoalDetail } = useFlags();
  const { handleSaveProjectStatus, getProjectStatuses } =
    useUpdateProjectStatus();

  const [displayCancelModal, setDisplayCancelModal] = useState(false);
  const [displayOnHoldModal, setDisplayOnHoldModal] = useState(false);
  const [displayCloseModal, setDisplayCloseModal] = useState(false);

  const projects = useSelector(selectFormattedGoalProjects);
  const selectedProject = useSelector(getOriginalProjectData);
  const projectIds = useMemo(
    () => projects.map((project) => project.id),
    [projects]
  );

  // `shallowEqual` because the selector maps ids to entities and so returns a
  // new array each call; without it this re-renders on every dispatched action.
  const projectPermissions = useSelector(
    (state: RootState) =>
      projectPermissionsSelectors.selectProjectPermissionsByIds(
        state,
        projectIds
      ),
    shallowEqual
  );

  const projectsWithPermissions = projects.map((project) => {
    return {
      ...project,
      permissions: projectPermissions.find(
        (permission) => permission.id === project.id
      ),
    };
  });

  useEffect(() => {
    if (projectIds.length > 0) {
      dispatch(getProjectPermissions({ projectIds }));
    }
  }, [projectIds, dispatch]);

  const onRefresh = useCallback(() => {
    dispatch(fetchGoalProjects({ goalId }));
  }, [dispatch, goalId]);

  const handleAfterStatusChange = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  const onOpenCancelModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCancelModal(true);
    },
    [dispatch]
  );

  const onOpenOnHoldModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayOnHoldModal(true);
    },
    [dispatch]
  );

  const onOpenCloseModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCloseModal(true);
    },
    [dispatch]
  );

  const handleCloseCancelModal = useCallback(() => {
    setDisplayCancelModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseOnHoldModal = useCallback(() => {
    setDisplayOnHoldModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseCloseModal = useCallback(() => {
    setDisplayCloseModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleConfirmCancelProject = useCallback(
    async (reason: string, archive: boolean) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'canceled',
          cancelReason: reason,
          cancellationDate: new Date().toISOString(),
          isArchived: archive,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayCancelModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const handleConfirmOnHoldProject = useCallback(
    async (holdReason: string) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'on_hold',
          holdReason,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayOnHoldModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const handleConfirmCloseProject = useCallback(
    async (comments: string, closingDate: string, archive: boolean) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'closed',
          finalComments: comments,
          closedDate: closingDate,
          isArchived: archive,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayCloseModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const sortProjects = (orderByParam: string, order: SortingType) => {
    dispatch(fetchGoalProjects({ goalId, orderByParam, order }));
  };

  const onRowClick = (canView: boolean, projectId: string) => {
    if (!canView || inlineEditingProjectStatusGoalDetail) return;

    if (canView && !inlineEditingProjectStatusGoalDetail) {
      history.push(`${PATHS.PROJECT_PAGE}/${projectId}`);
    }
  };

  const getProjectTitleCell = (canView: boolean, project: Project) => {
    const path = `${PATHS.PROJECT_PAGE}/${project.id}`;

    if (!canView) {
      return <div title={project.title}>{project.title}</div>;
    }

    if (canView && !inlineEditingProjectStatusGoalDetail) {
      return (
        <Link to={path} title={project.title}>
          {project.title}
        </Link>
      );
    }

    if (canView && inlineEditingProjectStatusGoalDetail) {
      return <LinkTableRowCell value={project.title} to={path} />;
    }
  };

  const getStatusColumnDependsOnFlag = (
    project: Project,
    canEditStatus: boolean,
    hasEditPermission: boolean,
    isOnlyStatusViewMode: boolean
  ) => {
    if (!inlineEditingProjectStatusGoalDetail)
      return getStatusColumn(project.status);

    return (
      <StatusTableRowCell
        value={project.status}
        customizedValue={(status) =>
          getStatusColumn(
            status,
            false,
            '',
            inlineEditingProjectStatusGoalDetail,
            canEditStatus
          )
        }
        options={getProjectStatuses(project)}
        isEditable={!isOnlyStatusViewMode}
        className='w-32'
        isTaskProjectDisabled={false}
        canInlineEditTask={true}
        onSave={(newValue) => {
          handleSaveProjectStatus(
            newValue,
            canEditStatus,
            project,
            onOpenCancelModal,
            onOpenOnHoldModal,
            onOpenCloseModal,
            onRefresh
          );
        }}
        canEdit={hasEditPermission}
        entityType={StatusInlineEditingEntityType.PROJECT}
        userPilotEventOrigin={PilotPagesToTrack.GOAL_DETAIL}
        isArchived={project.is_archived}
      />
    );
  };

  return (
    <div className='overflow-auto border rounded border-primary-lighter-two max-h-screen-minus-8rem'>
      <Table
        data-testid='goal-projects-table'
        className='border-0 w-full'
        canSelectRows={false}
        isSelectRowCellSticky
        data={{
          headData: {
            stickyHeader: true,
            handleSort: sortProjects,
            headCells: [
              {
                columnName: GOAL_PROJECTS_TABLE_COLUMNS.ID,
                content: intl.get('STRATEGY_GOALS.LINKED_PROJECTS.ID'),
              },
              {
                columnName: GOAL_PROJECTS_TABLE_COLUMNS.TITLE,
                content: intl.get('STRATEGY_GOALS.LINKED_PROJECTS.TITLE'),
              },
              {
                columnName: GOAL_PROJECTS_TABLE_COLUMNS.STATUS,
                content: intl.get('STRATEGY_GOALS.LINKED_PROJECTS.STATUS'),
              },
              {
                columnName: GOAL_PROJECTS_TABLE_COLUMNS.OWNER,
                content: intl.get('STRATEGY_GOALS.LINKED_PROJECTS.OWNER'),
              },
              {
                columnName: GOAL_PROJECTS_TABLE_COLUMNS.PROGRESS,
                content: intl.get('STRATEGY_GOALS.LINKED_PROJECTS.PROGRESS'),
              },
              {
                columnName: GOAL_PROJECTS_TABLE_COLUMNS.START_DATE,
                content: intl.get('STRATEGY_GOALS.LINKED_PROJECTS.START_DATE'),
              },
              {
                columnName: GOAL_PROJECTS_TABLE_COLUMNS.TARGET_COMPLETION_DATE,
                content: intl.get(
                  'STRATEGY_GOALS.LINKED_PROJECTS.TARGET_COMPLETION_DATE'
                ),
              },
            ],
          },
          rows: projectsWithPermissions.map((project) => {
            const canView = project?.permissions?.canView;
            const hasEditPermission = project?.permissions?.canEdit ?? false;
            const canEditStatus = hasEditPermission && !project.is_archived;
            const isOnlyStatusViewMode = !canEditStatus;

            return {
              id: project.id,
              className: classNames({
                'cursor-pointer':
                  canView && !inlineEditingProjectStatusGoalDetail,
                'cursor-not-allowed': !canView,
              }),
              onClick: () => onRowClick(canView, project.id),
              cells: [
                {
                  content: formatProjectNumber(project.projectNumber),
                },
                {
                  content: getProjectTitleCell(canView, project),
                  className: 'max-w-60 truncate',
                },
                {
                  content: getStatusColumnDependsOnFlag(
                    project,
                    canEditStatus,
                    hasEditPermission,
                    isOnlyStatusViewMode
                  ),
                  className: inlineEditingProjectStatusGoalDetail ? 'p-0' : '',
                },
                {
                  content: project.owners && getOwnerColumn(project.owners),
                },
                {
                  content: getProgress(
                    project.progress.totalTasks,
                    project.progress.completedTasks,
                    'w-22'
                  ),
                },
                {
                  content:
                    project.projectStartDate &&
                    moment(new Date(project.projectStartDate)).format(
                      DATE.SHORT_FORMAT
                    ),
                },
                {
                  content:
                    project.projectTargetCompletionDate &&
                    moment(
                      new Date(project.projectTargetCompletionDate)
                    ).format(DATE.SHORT_FORMAT),
                },
              ],
            };
          }),
        }}
        emptyComponent={
          <Typography
            className='flex justify-center w-full my-4'
            variant='body2'
          >
            {intl.get('STRATEGY_GOALS.LINKED_PROJECTS.NO_LINKED_PROJETS')}
          </Typography>
        }
      />
      {selectedProject && (
        <>
          <CancelProject
            shouldDisplayModal={displayCancelModal}
            closeModalCallback={handleCloseCancelModal}
            confirmCancelProject={handleConfirmCancelProject}
            cancelReason={selectedProject.cancel_reason}
            cancellationDate={selectedProject.cancellation_date}
          />
          <PutProjectOnHoldModal
            shouldDisplayModal={displayOnHoldModal}
            onClose={handleCloseOnHoldModal}
            onCancelCallback={handleCloseOnHoldModal}
            onConfirm={handleConfirmOnHoldProject}
            holdReason={selectedProject.hold_reason}
          />
          <CloseProject
            shouldDisplayModal={displayCloseModal}
            closeModalCallback={handleCloseCloseModal}
            confirmCloseProject={handleConfirmCloseProject}
            project={selectedProject}
          />
        </>
      )}
    </div>
  );
};
export default GoalProjectsTable;
